import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {HelpersService} from './helpers.service';
import {MapMarker} from '../models/marker';
import {
  ico_active,
  ico_active_p2p,
  ico_interest_point,
  ico_interest_point_smaller,
  ico_pcn,
  ico_pcn_smaller,
  ico_picked,
  ico_picked_p2p,
  ico_pop_agf,
  ico_pop_agf_smaller,
  ico_pop_naz,
  ico_pop_naz_smaller,
  ico_preview,
  icon_building_ftth_blue,
  icon_building_ftth_green,
  icon_building_ftth_pink,
  icon_building_ftth_yellow,
  icon_building_man_blue,
  icon_building_man_green,
  icon_building_man_pink,
  icon_cluster_blue,
  icon_cluster_green,
  icon_cluster_pink,
  icon_cluster_yellow,
  iconMarker,
} from 'src/configs/map_configuration';

@Injectable()
export class MapService extends HelpersService {
	private geocoder = new google.maps.Geocoder()
	public pointSaved: any
	public pops: any
	public pois: any
	public mapButtons = {
		pops: {
			active: true,
			tooltip: "Show / hide POP Urban Area",
			icon: "./assets/imgs/markers/ico-pop-active.png?v={{assets_version}}"
		},
		poi: {
			active: false,
			tooltip: "Show / hide POI",
			icon: "./assets/imgs/markers/ico-Point-Of-Interest-active.png?v={{assets_version}}"
		},
		covered_ab: {
			active: true,
			tooltip: "Show / hide Coverage Map Urban Area",
			icon: "./assets/imgs/markers/ico-mappa-copertura-active.png?v={{assets_version}}"
		},
		covered_cd: {
			active: true,
			tooltip: "Show / hide Coverage Map Rural Area",
			icon: "./assets/imgs/markers/ico-cd-map.png?v={{assets_version}}"
		},
		network: {
			active: false,
			tooltip: "Transport Network",
			icon: "./assets/imgs/markers/ico-rete-trasporto-active.png?v={{assets_version}}"
		},
		prospect: {
			active: false,
			tooltip: "Show / hide Planned Coverage Map",
			icon: "./assets/imgs/markers/ico-prospect-map.png?v={{assets_version}}"
		}
	}

	public activeButtonsLength: any
	public get activeButtons() {
		const buttons = []
		if (environment.show_agf) {
			buttons.push("CoveredAGF")
		}
		if (this.mapButtons.covered_ab.active) {
			buttons.push("CoveredAB")
		}
		if (this.mapButtons.covered_cd.active) {
			buttons.push("CoveredCD")
		}
		if (this.mapButtons.prospect.active) {
			buttons.push("Designed")
		}
		return buttons
	}

	getPops(): Observable<any> {
		var types = ["AB", "CD"]
		if (environment.show_agf) {
			types.push("AGF")
		}
		if (this.pops) {
			return of(this.pops)
		} else {
			var params = ""
			if (types && types.length) {
				params = "?"
				for (var t of types) {
					params += "&types[]=" + t
				}
			}
			return this.http
				.get(environment.DOMAIN_URL + "/api2/marketing/pops" + params, this.getHTTPHeader(true))
				.pipe(
					map(data => {
						this.pops = data
						return data
					}),
					catchError(error => {
						return this.handleError(error, "Pop")
					})
				)
		}
	}

	searchBuildingPlaceholders(bounds: google.maps.LatLngBounds, zoom, activeButtons): Observable<any> {
		let typeParams = ""
		if (activeButtons) {
			for (const p of activeButtons) {
				typeParams += "&types[]=" + p
			}
		}
		const bbox =
			bounds.getSouthWest().lng() +
			"," +
			bounds.getSouthWest().lat() +
			"," +
			bounds.getNorthEast().lng() +
			"," +
			bounds.getNorthEast().lat()
		const url =
			environment.DOMAIN_URL + "/api2/marketing/building" + "?in_bbox=" + bbox + "&zoom=" + zoom + typeParams
		return this.http.get<any>(url, this.getHTTPHeader(true)).pipe(
			map(data => {
				return data
			}),
			catchError(error => {
				return this.handleError(error, "NumeroCivico")
			})
		)
	}

	getBuildingByCoordinates(lat, lng): Observable<any> {
		let typeParams = "&types[]=CoveredAB&types[]=CoveredCD"
		if (environment.show_agf) {
			typeParams += "&types[]=CoveredAGF"
		}
		return this.http
			.get<any>(
				`${environment.DOMAIN_URL}/api2/marketing/building/coordinates-search?lng=${lng}&lat=${lat}${typeParams}`
			)
			.pipe(
				map(data => {
					return data
				}),
				catchError(error => {
					return this.handleError(error, "Building")
				})
			)
	}

	getKml(types): Observable<any> {
		let parameters = ""
		if (types) {
			for (const t of types) {
				parameters += "&types[]=" + t
			}
			parameters = parameters.substring(1)
		}
		return this.http
			.get<any>(
				environment.BASE_SERVER_URL + "/api2/marketing/kml/list/italy" + "?" + parameters,
				this.getHTTPHeader(true)
			)
			.pipe(
				map(result => {
					return result
				})
			)
			.pipe(
				catchError(error => {
					return this.handleError(error, "Kml")
				})
			)
	}

	public getNetwork(): Observable<any> {
		return this.http.get(environment.DOMAIN_URL + "/api2/marketing/data/network/transport.json")
	}

	public getNetworkNational(): Observable<any> {
		return this.http.get(environment.DOMAIN_URL + "/api2/marketing/data/network/national-transport.json")
	}

	createP2pQuotation(params: any): Observable<any> {
		return this.http
			.post<any>(environment.DOMAIN_URL + "/api2/marketing/p2p", params, this.getHTTPHeader(true))
			.pipe(
				map(data => {
					return data
				}),
				catchError(error => {
					return this.handleError(error, "createP2pQuotation")
				})
			)
	}

	createBeaQuotation(params: any): Observable<any> {
		return this.http
			.post<any>(environment.DOMAIN_URL + "/api2/marketing/p2p/single", params, this.getHTTPHeader(true))
			.pipe(
				map(data => {
					return data
				}),
				catchError(error => {
					return this.handleError(error, "createBeaQuotation")
				})
			)
	}

	getPois(): Observable<any> {
		if (this.pois) {
			return of(this.pois)
		} else {
			const useOloCode = this.userData.olocode ? `?olo_code=${this.userData.olocode}` : ""
			return this.http
				.get<any>(environment.DOMAIN_URL + "/api2/marketing/poi" + useOloCode, this.getHTTPHeader(true))
				.pipe(
					map(data => {
						this.pois = data
						return data
					})
				)
		}
	}

	convertCoordinates(lat, lng): Observable<any> {
		let url = `${environment.DOMAIN_URL}/api2/coordinates/convert?lat=${lat}&lng=${lng}`
		return this.http.get<any>(url, this.getHTTPHeader(true)).pipe(
			map(data => {
				return data
			}),
			catchError(error => {
				this.showError(error.message)
				return this.handleError(error, "convertCoordinates")
			})
		)
	}

	public pointLabel(results: google.maps.GeocoderResult[]) {
		return results && results[0] ? results[0].formatted_address : ""
	}

	public pointInfo(results: google.maps.GeocoderResult[]) {
		const ret: any = {}
		ret.comune = this.getInfo(results, ["administrative_area_level_3"])
		ret.provincia = this.getInfo(results, ["administrative_area_level_2"], "short_name")
		ret.cap = this.getInfo(results, ["postal_code"])
		ret.country = this.getInfo(results, ["country"], "short_name")
		ret.street_number = this.getInfo(results, ["street_number"], "short_name")
		ret.street = this.getInfo(results, ["route", "town_square"])
		ret.civic_number = this.getInfo(results, ["street_number"])
		ret.label = results && results[0] ? results[0].formatted_address : ""
		return ret
	}

	public getInfo(results, type, attr = "long_name") {
		let ret = ""
		if (results && results.length) {
			for (const result of results) {
				let found = null
				for (const t of type) {
					if (result.address_components && result.address_components.length) {
						found = result.address_components.filter(a => a.types.includes(t))[0]
						if (found) {
							ret = found[attr]
							return ret
						} else {
							ret = ""
						}
					}
				}
			}
		}
		return ret
	}

	public getInfoFromSingleAddressComponent(address_components, type, attr = "long_name") {
		let ret = ""
		if (address_components && address_components.length) {
			let found = null
			for (const t of type) {
				found = address_components.filter(a => a.types.includes(t))[0]
				if (found) {
					ret = found[attr]
					return ret
				} else {
					ret = ""
				}
			}
		}
		return ret
	}

	public findIconMarkerByType(marker, end) {
		// Area type is relevant for POP only
		const area = marker.type && marker.type === "POP" ? marker.area_type : null
		const type = marker.type.split(" ")[0]
		return iconMarker[area][type][end]
	}

	// Markers model

	public popToMarker(pop) {
		let marker: MapMarker
		const range = { min: 0, max: null }
		if (!pop.show) {
			range.min = null
		} else if (pop.show && !pop.nazionale) {
			range.min = 9
		}
		var areaType
		switch (pop.type) {
			case "Secondary":
				areaType = "covered_cd"
				break
			case "AGF Ibrido":
			case "AGF":
				areaType = "covered_agf"
				break
			default:
				areaType = "covered_ab"
				break
		}
		marker = {
			area_type: areaType,
			icons: [
				{
					area_type: "covered_cd",
					zoom_from: 1,
					zoom_to: 11,
					path: ico_pcn_smaller
				},
				{ area_type: "covered_cd", zoom_from: 12, zoom_to: 20, path: ico_pcn },
				{
					area_type: "covered_ab",
					zoom_from: 1,
					zoom_to: 11,
					path: ico_pop_naz_smaller
				},
				{
					area_type: "covered_ab",
					zoom_from: 12,
					zoom_to: 20,
					path: ico_pop_naz
				},
				{
					area_type: "covered_agf",
					zoom_from: 1,
					zoom_to: 11,
					path: ico_pop_agf_smaller
				},
				{
					area_type: "covered_agf",
					zoom_from: 12,
					zoom_to: 20,
					path: ico_pop_agf
				}
			],
			type: "POP",
			coordinates: { lat: pop.lat, lng: pop.lng },
			id: pop.id,
			label: pop.nome,
			range: range,
			can_hover: true,
			can_click: true
		}
		return marker
	}

	public poiToMarker(poi) {
		let marker: MapMarker
		marker = {
			area_type: null,
			icons: [
				{ zoom_from: 1, zoom_to: 11, path: ico_interest_point_smaller },
				{ zoom_from: 12, zoom_to: 20, path: ico_interest_point }
			],
			type: "POI",
			coordinates: poi.coordinates,
			id: poi.id,
			label: poi.fullname,
			range: { min: 0, max: null },
			can_hover: true,
			can_click: true
		}
		return marker
	}

  // Click on map
  public picked2Marker(picked) {
    let marker: MapMarker;
    marker = {
      area_type: null,
      type: "PICKED",
      coordinates: { lat: picked.latLng.lat(), lng: picked.latLng.lng() },
      id: null,
      label: picked.label,
      range: { min: 0, max: null },
      can_hover: false,
      can_click: false,
      icons: [{ zoom_from: 1, zoom_to: 30, path: ico_preview }],
    };
    return marker;
  }

	// Address search
	public place2Marker(place, type) {
		let marker: MapMarker
		let pathIcon
		switch (type) {
			case "POINT_PLACE":
				pathIcon = ico_picked
				break
			case "POINT_PLACE_A":
				pathIcon = ico_picked_p2p
				break
			case "POINT_PLACE_B":
				pathIcon = ico_picked
				break
		}
		marker = {
			area_type: null,
			type: type,
			coordinates:
				place.geometry && place.geometry.location && place.geometry.location.lat && place.geometry.location.lng
					? {
							lat: place.geometry.location.lat(),
							lng: place.geometry.location.lng()
						}
					: null,
			id: null,
			label: place.formatted_address,
			range: { min: 0, max: null },
			can_hover: false,
			can_click: false,
			icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
			civico: null,
			pop: false,
			interest_point: false,
			cap: this.getInfoFromSingleAddressComponent(place.address_components, ["postal_code"]),
			comune: this.getInfoFromSingleAddressComponent(place.address_components, [
				"administrative_area_level_3",
				"locality"
			]),
			provincia: this.getInfoFromSingleAddressComponent(
				place.address_components,
				["administrative_area_level_2"],
				"short_name"
			),
			country: this.getInfoFromSingleAddressComponent(place.address_components, ["country"]),
			street: this.getInfoFromSingleAddressComponent(place.address_components, ["route", "town_square"]),
			street_number: this.getInfoFromSingleAddressComponent(place.address_components, ["street_number"]),
			civic_number: this.getInfoFromSingleAddressComponent(place.address_components, ["street_number"])
		}
		return marker
	}

	// Coordinates search
	public coordinates2Marker(place, type) {
		let marker: MapMarker
		let pathIcon
		switch (type) {
			case "POINT_PLACE":
				pathIcon = ico_picked
				break
			case "POINT_PLACE_A":
				pathIcon = ico_picked_p2p
				break
			case "POINT_PLACE_B":
				pathIcon = ico_picked
				break
		}
		marker = {
			area_type: null,
			type: type,
			coordinates: {
				lat: place.coordinates.lat(),
				lng: place.coordinates.lng()
			},
			id: null,
			label: place.formatted_address,
			range: { min: 0, max: null },
			can_hover: false,
			can_click: true,
			icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
		}
		return marker
	}

	// Toast, civici
	public marker2MarkerPoint(point: MapMarker, type, altIcon?) {
		let marker: MapMarker
		let pathIcon
		switch (type) {
			case "POINT_PICKED":
				pathIcon = ico_picked
				break
			case "POINT_PICKED_A":
				pathIcon = ico_picked_p2p
				break
			case "POINT_PICKED_B":
				pathIcon = ico_picked
				break
			case "POINT_CIVIC":
				pathIcon = ico_active
				break
			case "POINT_CIVIC_A":
				pathIcon = ico_active_p2p
				break
			case "POINT_CIVIC_B":
				pathIcon = ico_active
				break
		}
		marker = {
			area_type: null,
			type: type,
			coordinates: point.coordinates,
			id: point.id,
			label: point.label,
			labelOnInput: point.labelOnInput ? point.labelOnInput : null,
			range: { min: 0, max: null },
			can_hover: false,
			can_click: false,
			icons: [{ zoom_from: 1, zoom_to: 30, path: altIcon ? altIcon : pathIcon }]
		}
		return marker
	}

  public cluster2Marker(cluster) {
    let pathIcon;
    let marker: any = {
      coordinates: {
        lat: cluster.coordinates.coordinates[1],
        lng: cluster.coordinates.coordinates[0],
      },
      id: null,
      can_hover: false,
      can_click: true,
      label: String(cluster.tot_buildings),
      range: { min: 0, max: null },
      label_inside: true,
      type: "cluster",
    };
    switch (cluster.type) {
      case "CoveredAB":
        pathIcon = icon_cluster_pink;
        marker = {
          ...marker,
          area_type: "covered_ab",
          icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
        };
        break;
      case "CoveredCD":
        pathIcon = icon_cluster_green;
        marker = {
          ...marker,
          area_type: "covered_cd",
          icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
        };
        break;
      case "CoveredAGF":
        pathIcon = icon_cluster_yellow;
        marker = {
          ...marker,
          area_type: "covered_agf",
          icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
        };
        break;
      case "Designed":
        pathIcon = icon_cluster_blue;
        marker = {
          ...marker,
          area_type: "prospect",
          icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
        };
        break;
    }
    return marker;
  }

  public civic2Marker(civic) {
    if (civic.selected_building) {
      const civicTemp = civic.selected_building;
      let marker: any = {
        coordinates: {
          lat: civicTemp.coordinates.coordinates[1],
          lng: civicTemp.coordinates.coordinates[0],
        },
        id: civicTemp.id,
        can_hover: true,
        can_click: true,
        type: civicTemp.tipologia_building,
        label: civicTemp.building_labels.join("<br />"),
        labelOnInput:
          civicTemp.indirizzo && civicTemp.indirizzo.via
            ? null
            : civicTemp.coordinates.coordinates[1] +
              ", " +
              civicTemp.coordinates.coordinates[0],
        range: { min: 18, max: 30 },
      };
      let pathIcon;
      switch (civicTemp.type) {
        case "CoveredAB":
          pathIcon =
            civicTemp.tipologia_building === "FTTH"
              ? icon_building_ftth_pink
              : icon_building_man_pink;
          marker = {
            ...marker,
            area_type: "covered_ab",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
        case "CoveredCD":
          pathIcon =
            civicTemp.tipologia_building === "MAN C&D"
              ? icon_building_man_green
              : icon_building_ftth_green;
          marker = {
            ...marker,
            area_type: "covered_cd",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
        case "CoveredAGF":
          pathIcon = icon_building_ftth_yellow;
          marker = {
            ...marker,
            area_type: "covered_agf",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
        case "Designed":
          pathIcon =
            civicTemp.tipologia_building === "FTTH"
              ? icon_building_ftth_blue
              : icon_building_man_blue;
          marker = {
            ...marker,
            area_type: "prospect",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
      }
      if (civic.stato_building == 302) {
        marker.icons[0].path = icon_building_man_green;
      }
      return marker;
    } else {
      let marker: any = {
        coordinates: {
          lat: civic.coordinates.coordinates[1],
          lng: civic.coordinates.coordinates[0],
        },
        id: civic.id,
        can_hover: true,
        can_click: true,
        type: civic.tipologia_building,
        label: civic.building_labels.join("<br />"),
        labelOnInput:
          civic.indirizzo && civic.indirizzo.via
            ? null
            : civic.coordinates.coordinates[1] +
              ", " +
              civic.coordinates.coordinates[0],
        range: { min: 18, max: 30 },
      };
      let pathIcon;
      switch (civic.type) {
        case "CoveredAB":
          pathIcon =
            civic.tipologia_building === "FTTH"
              ? icon_building_ftth_pink
              : icon_building_man_pink;
          marker = {
            ...marker,
            area_type: "covered_ab",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
        case "CoveredCD":
          pathIcon =
            civic.tipologia_building === "MAN C&D"
              ? icon_building_man_green
              : icon_building_ftth_green;
          marker = {
            ...marker,
            area_type: "covered_cd",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
        case "CoveredAGF":
          pathIcon = icon_building_ftth_yellow;
          marker = {
            ...marker,
            area_type: "covered_agf",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
        case "Designed":
          pathIcon =
            civic.tipologia_building === "FTTH"
              ? icon_building_ftth_blue
              : icon_building_man_blue;
          marker = {
            ...marker,
            area_type: "prospect",
            icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }],
          };
          break;
      }
      if (civic.stato_building == 302) {
        marker.icons[0].path = icon_building_man_green;
      }
      return marker;
    }
  }

  markerToRequest(marker): Observable<any> {
    const req: any = {
      id: marker.id,
      label: marker.labelOnInput ? marker.labelOnInput : marker.label,
      lat: marker.coordinates.lat,
      lng: marker.coordinates.lng,
      coordinate: {
        type: "Point",
        coordinates: [marker.coordinates.lng, marker.coordinates.lat],
      },
      civico: null,
      pop: null,
      interest_point: false,
    };
    switch (marker.typeForBe) {
      case "FTTH":
      case "MAN":
      case "FTTH C&D":
      case "MAN C&D":
      case "BUILDING":
        req.type = "BUILDING";
        req.civico = marker.id;
        req.interest_point = false;
        break;
      case "POI":
        req.type = "POI";
        req.interest_point = marker.id;
        req.interest_point = true;
        break;
      case "POP":
        req.type = "POP";
        req.pop = marker.id;
        req.interest_point = false;
        break;
      default:
        req.type = "POINT";
        req.interest_point = false;
        req.coordinates_type = marker.coordinates_type;
        break;
    }

    // verifica se è un indirizzo.
    // Solo gli indirizzi hanno i dettagli del geocode popolati.
    // Country è il parametro più generico.
    if (marker.country) {
      return new Observable((observer) => {
        req.cap = marker.cap ? marker.cap : null;
        req.comune = marker.comune ? marker.comune : null;
        req.provincia = marker.provincia ? marker.provincia : null;
        req.country = marker.country;
        req.street_number = marker.street_number ? marker.street_number : null;
        req.street = marker.street ? marker.street : null;
        req.civic_number = marker.civic_number ? marker.civic_number : null;
        observer.next(req);
        observer.complete();
      });
    }

    return new Observable((observer) => {
      this.geocoder.geocode(
        { location: new google.maps.LatLng(req.lat, req.lng) },
        (results) => {
          const res = this.pointInfo(results);
          req.cap = res.cap;
          req.comune = res.comune;
          req.provincia = res.provincia;
          req.country = res.country;
          req.street_number = res.street_number;
          req.street = res.street;
          req.civic_number = res.civic_number;
          observer.next(req);
          observer.complete();
        }
      );
    });
  }

  markerToPointRequest(res) {

    return  {
      coordinates: res.coordinate.coordinates,
      label: res.label,
      id: res.id,
      type: res.type,
      comune: res.comune,
      provincia: res.provincia,
      cap: res.cap,
      street: res.street,
      street_number: res.street_number,
      research_method: res.research_method,
    };
  }

  createRequestBody(points, accessType?,project?,at_request_of?) {
    return {
      points: [points],
      mode: null,
      service_type: null,
      service_sub_type: null,
      access_type: accessType ? accessType : null,
      at_request_of: at_request_of ? at_request_of : null,
      project: project ? project : null,
    };
  }

  pathToPolyLineP2P(path, strokeColor, strokeOpacity, strokeWeight) {
    const ret = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor,
      strokeOpacity,
      strokeWeight,
      zIndex: 500,
    });
    return ret;
  }
}
